import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Clock, XCircle } from 'lucide-react';
import { api } from '../lib/api';
import { useCart } from '../context/CartContext';

const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

const STATES = {
    paid: { icon: CheckCircle2, color: 'text-emerald-600', title: 'Order confirmed', text: 'Payment received. A receipt is on its way to' },
    processing: { icon: Clock, color: 'text-amber-600', title: 'Order received', text: 'Your bank payment is processing (usually 1–4 business days). We will email' },
    failed: { icon: XCircle, color: 'text-red-600', title: 'Payment not completed', text: 'Your payment did not go through. No charge was made to' },
    awaiting_payment: { icon: Clock, color: 'text-amber-600', title: 'Order received', text: 'Your order is reserved. Payment details have been emailed to' },
};

const PayInfo = ({ order }) => (
    <div className="mt-6 border-2 border-brand-primary p-6" data-testid="order-pay-info">
        <div className="label-caps text-xs text-brand-primary mb-2">Next step: payment</div>
        {order.payment_method === 'wire' ? (
            <>
                <p className="text-neutral-700 text-sm mb-3">Send a bank wire / ACH transfer for <b>{money(order.total)}</b> and put <b>{order.order_number}</b> in the reference.</p>
                <pre className="whitespace-pre-wrap font-mono text-sm bg-neutral-50 border border-neutral-200 p-4 text-neutral-900" data-testid="order-wire-instructions">
                    {order.wire_instructions || 'Our team will email you our bank wire details shortly.'}
                </pre>
            </>
        ) : (
            <>
                <p className="text-neutral-700 text-sm">We&apos;ll email you an invoice where you can pay by credit card or ACH bank transfer. We ship as soon as it&apos;s paid.</p>
                {order.wave_payment_link?.startsWith('http') && (
                    <a href={order.wave_payment_link} target="_blank" rel="noreferrer" className="mt-4 inline-block bg-neutral-900 hover:bg-brand-primary text-on-dark font-bold px-6 py-3 label-caps transition-colors" data-testid="order-wave-link">
                        Pay now by card
                    </a>
                )}
            </>
        )}
    </div>
);

const OrderSuccess = () => {
    const [params] = useSearchParams();
    const { clear } = useCart();
    const [order, setOrder] = useState(null);
    const [error, setError] = useState('');
    const sessionId = params.get('session_id');
    const orderId = params.get('order');

    useEffect(() => {
        let tries = 0;
        let timer;
        const url = sessionId ? `/shop/orders/stripe/${sessionId}` : `/shop/orders/${orderId}`;
        const poll = async () => {
            try {
                const { data } = await api.get(url);
                setOrder(data);
                if (['paid', 'processing', 'awaiting_payment'].includes(data.payment_status)) { clear(); return; }
                if (++tries < 10) timer = setTimeout(poll, 2000);
                else setError('Your payment is still being confirmed — you will get an email as soon as it clears.');
            } catch { setError('We could not find this order.'); }
        };
        poll();
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, orderId]);

    const st = order && (STATES[order.payment_status] || (['expired'].includes(order.payment_status) ? STATES.failed : null));

    return (
        <div className="container-page pt-32 pb-24 max-w-3xl" data-testid="order-success-page">
            {error && <p className="text-red-600" data-testid="order-error">{error}</p>}
            {!error && !st && (
                <div className="flex items-center gap-3 text-neutral-600" data-testid="order-checking"><Loader2 className="h-5 w-5 animate-spin" /> Confirming your payment…</div>
            )}
            {st && (
                <div data-testid={`order-status-${order.payment_status}`}>
                    <st.icon className={`h-14 w-14 ${st.color}`} />
                    <h1 className="mt-6 font-display font-black uppercase tracking-tighter text-5xl text-neutral-900">{st.title}</h1>
                    <p className="mt-3 text-neutral-600">{st.text} <b>{order.email}</b>.</p>
                    {order.payment_status === 'awaiting_payment' && <PayInfo order={order} />}
                    <div className="mt-8 border border-neutral-200 p-6 bg-neutral-50">
                        <div className="label-caps text-xs text-neutral-500">Order number</div>
                        <div className="font-display font-black text-2xl text-neutral-900" data-testid="order-number">{order.order_number}</div>
                        <div className="mt-4 space-y-1 text-sm text-neutral-700">
                            {order.lines.map((l, i) => <div key={i} className="flex justify-between"><span>{l.qty} × {l.name}</span><span>{money(l.line_total)}</span></div>)}
                            <div className="flex justify-between border-t border-neutral-200 pt-2 mt-2"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
                            <div className="flex justify-between"><span>Shipping</span><span data-testid="order-shipping">{order.shipping ? money(order.shipping) : 'FREE'}</span></div>
                            {order.tax > 0 && <div className="flex justify-between"><span>{order.tax_label}</span><span data-testid="order-tax">{money(order.tax)}</span></div>}
                            <div className="flex justify-between border-t border-neutral-200 pt-2 mt-2 font-bold text-neutral-900"><span>Total</span><span data-testid="order-total">{money(order.total)}</span></div>
                        </div>
                    </div>
                    <Link to={st === STATES.failed ? '/checkout' : '/shop'} className="mt-8 inline-block bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-6 py-3 label-caps transition-colors" data-testid="order-continue">
                        {st === STATES.failed ? 'Try again' : 'Continue shopping'}
                    </Link>
                </div>
            )}
        </div>
    );
};

export default OrderSuccess;
